import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { Sun, Save, Sparkles, Loader2, Lock, Bell, CheckSquare, Check, ChevronDown, Shield, CreditCard, Star, Users, Minus, Plus, Globe, Share2, Copy, Trash2, ExternalLink, Clock, FileText, Download } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../lib/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';

const fmt = (n) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n || 0);

function PlanCard({ plan, currentPlan, onUpgrade, onStartTrial, trialUsed, trialLoading, planStatus }) {
  const [extraUsers, setExtraUsers] = useState(0);
  const isCurrent = plan.name.toLowerCase() === currentPlan;
  const extraRate = plan.extraUserRate || 0;
  const totalPrice = plan.priceNum + (extraRate * extraUsers);
  const canTrial = !trialUsed && plan.priceNum > 0 && currentPlan === 'individual';
  const isPaid = planStatus === 'paid';

  return (
    <div className={`bg-white rounded-2xl border overflow-hidden flex flex-col ${plan.popular ? 'border-emerald-500' : 'border-slate-200'}`} data-testid={`settings-plan-${plan.name.toLowerCase()}`}>
      {plan.popular && (
        <div className="flex justify-center -mt-px">
          <div className="bg-emerald-500 px-4 py-1 rounded-b-lg text-xs font-semibold text-white flex items-center gap-1.5">
            <Star className="w-3 h-3" /> Most Popular
          </div>
        </div>
      )}
      <div className="p-6 flex flex-col flex-1 text-center">
        <h4 className="text-lg font-bold text-slate-900">{plan.name}</h4>
        <p className="text-sm text-slate-500 mb-4">{plan.subtitle}</p>
        <div className="mb-2">
          <span className="text-3xl font-bold text-slate-900">{plan.priceNum === 0 ? 'Free' : `$${totalPrice.toFixed(2)}`}</span>
          {plan.priceNum > 0 && <span className="text-slate-500 text-sm">/month</span>}
        </div>
        {plan.priceNum === 0 && <p className="text-xs text-slate-400 mb-3">Free forever</p>}
        {extraUsers > 0 && plan.priceNum > 0 && (
          <p className="text-xs text-emerald-600 mb-3">Base ${plan.priceNum.toFixed(2)} + {extraUsers} extra user{extraUsers > 1 ? 's' : ''} (${(extraRate * extraUsers).toFixed(2)})</p>
        )}
        {!extraUsers && plan.priceNum > 0 && <div className="mb-3" />}

        {/* Feature list */}
        <div className="space-y-2.5 text-left flex-1">
          {plan.features.map((f, i) => (
            <div key={i} className="flex items-start gap-2">
              <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-slate-600">{f}</span>
            </div>
          ))}
        </div>

        {/* Extra Users Selector - only for paid plans */}
        {plan.maxExtraUsers > 0 && (
          <div className="mt-5 p-3 bg-slate-50 rounded-xl border border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-700">Extra Users</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setExtraUsers(Math.max(0, extraUsers - 1))}
                  disabled={extraUsers === 0}
                  className="w-7 h-7 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  data-testid={`plan-${plan.name.toLowerCase()}-minus`}
                >
                  <Minus className="w-3.5 h-3.5" />
                </button>
                <span className="w-6 text-center text-sm font-bold text-slate-900" data-testid={`plan-${plan.name.toLowerCase()}-extra-count`}>{extraUsers}</span>
                <button
                  onClick={() => setExtraUsers(Math.min(plan.maxExtraUsers, extraUsers + 1))}
                  disabled={extraUsers >= plan.maxExtraUsers}
                  className="w-7 h-7 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  data-testid={`plan-${plan.name.toLowerCase()}-plus`}
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-1.5 text-left">Up to {plan.maxExtraUsers} extra at ${extraRate.toFixed(2)}/user/month</p>
          </div>
        )}

        {/* Action Buttons */}
        {isCurrent && extraUsers === 0 ? (
          <div className="mt-4 py-2.5 bg-emerald-50 text-emerald-600 rounded-xl text-sm font-medium text-center">Current Plan</div>
        ) : plan.priceNum === 0 ? (
          <div className="mt-4 py-2.5 bg-slate-50 text-slate-400 rounded-xl text-sm font-medium text-center">Free Forever</div>
        ) : (
          <div className="mt-4 space-y-2">
            {/* Start Trial button (if eligible) */}
            {canTrial && (
              <button
                onClick={() => onStartTrial(plan.name.toLowerCase())}
                disabled={trialLoading}
                className="w-full py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-50"
                data-testid={`trial-${plan.name.toLowerCase()}`}
              >
                {trialLoading ? 'Starting...' : 'Start 7-Day Free Trial'}
              </button>
            )}
            {/* Upgrade/Subscribe button */}
            <button
              onClick={() => onUpgrade(plan.name.toLowerCase(), extraUsers, totalPrice)}
              className={`w-full py-2.5 rounded-xl text-sm font-medium transition-colors ${canTrial ? 'bg-slate-100 hover:bg-slate-200 text-slate-700' : 'bg-emerald-500 hover:bg-emerald-600 text-white'}`}
              data-testid={`settings-upgrade-${plan.name.toLowerCase()}`}
            >
              {isCurrent && extraUsers > 0 ? `Add ${extraUsers} User${extraUsers > 1 ? 's' : ''} — ${fmt(totalPrice)}/mo` : `Subscribe — ${fmt(totalPrice)}/mo`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function PayPalCheckoutModal({ plan, extraUsers, totalPrice, onClose, onSuccess }) {
  const [processing, setProcessing] = useState(false);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="paypal-checkout-modal">
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden">
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-slate-900">Complete Payment</h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors text-xl leading-none">&times;</button>
          </div>
        </div>

        <div className="p-6">
          {/* Order Summary */}
          <div className="bg-slate-50 rounded-xl p-4 mb-6 border border-slate-100">
            <p className="text-sm font-semibold text-slate-900 mb-2">Order Summary</p>
            <div className="flex justify-between text-sm text-slate-600">
              <span>ExpensIQ {plan.charAt(0).toUpperCase() + plan.slice(1)} Plan</span>
              <span className="font-medium">{fmt(totalPrice - (extraUsers > 0 ? extraUsers * (plan === 'basic' ? 4.95 : 9.95) : 0))}</span>
            </div>
            {extraUsers > 0 && (
              <div className="flex justify-between text-sm text-slate-600 mt-1">
                <span>{extraUsers} extra user{extraUsers > 1 ? 's' : ''}</span>
                <span className="font-medium">{fmt(extraUsers * (plan === 'basic' ? 4.95 : 9.95))}</span>
              </div>
            )}
            <hr className="my-2 border-slate-200" />
            <div className="flex justify-between text-sm font-bold text-slate-900">
              <span>Total (monthly)</span>
              <span>{fmt(totalPrice)}</span>
            </div>
          </div>

          {/* PayPal Payment Button */}
          <button
            onClick={async () => {
              setProcessing(true);
              try {
                const { data } = await api.post('/api/payments/create', { plan, extra_users: extraUsers, return_origin: window.location.origin });
                if (data.approval_url) {
                  window.location.href = data.approval_url;
                }
              } catch {
                toast.error('Failed to initiate payment');
                setProcessing(false);
              }
            }}
            disabled={processing}
            className="w-full py-3.5 bg-[#0070ba] hover:bg-[#003087] text-white rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="paypal-checkout-button"
          >
            {processing ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting to PayPal...</>
            ) : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.816-5.09a.932.932 0 0 1 .923-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z"/></svg>
                Pay with PayPal — {fmt(totalPrice)}/mo
              </>
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function SecurityTab() {
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [loading, setLoading] = useState(false);

  const inputClass = "w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500";

  const handleChangePassword = async () => {
    if (!currentPw || !newPw || !confirmPw) { toast.error('Please fill all password fields'); return; }
    if (newPw.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    if (newPw !== confirmPw) { toast.error('New passwords do not match'); return; }
    setLoading(true);
    try {
      await api.post('/api/auth/change-password', { current_password: currentPw, new_password: newPw });
      toast.success('Password updated successfully');
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update password');
    }
    setLoading(false);
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-6 lg:p-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center"><Shield className="w-5 h-5 text-slate-600" /></div>
        <div><h3 className="text-lg font-bold text-slate-900">Security Settings</h3><p className="text-sm text-slate-500">Manage your account security</p></div>
      </div>
      <div className="space-y-6">
        <div className="p-5 bg-slate-50 rounded-xl border border-slate-100">
          <div className="flex items-center gap-3 mb-4"><Lock className="w-5 h-5 text-slate-600" /><p className="font-semibold text-slate-900">Change Password</p></div>
          <div className="space-y-3">
            <input type="password" placeholder="Current password" value={currentPw} onChange={e => setCurrentPw(e.target.value)} className={inputClass} data-testid="settings-current-password" />
            <input type="password" placeholder="New password" value={newPw} onChange={e => setNewPw(e.target.value)} className={inputClass} data-testid="settings-new-password" />
            <input type="password" placeholder="Confirm new password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} className={inputClass} data-testid="settings-confirm-password" />
          </div>
          <button onClick={handleChangePassword} disabled={loading} className="mt-4 px-5 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50" data-testid="settings-change-password-button">
            {loading ? 'Updating...' : 'Update Password'}
          </button>
        </div>
        <div className="flex items-center justify-between p-5 bg-slate-50 rounded-xl border border-slate-100">
          <div><p className="font-semibold text-slate-900">Two-Factor Authentication</p><p className="text-sm text-slate-500">Add an extra layer of security to your account</p></div>
          <span className="text-xs font-medium text-slate-400 bg-slate-200 px-3 py-1 rounded-full">Coming Soon</span>
        </div>
      </div>
    </div>
  );
}

export default function Settings() {
  const { user, checkAuth } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: settings = {} } = useQuery({ queryKey: ['settings'], queryFn: async () => { const { data } = await api.get('/api/settings'); return data; } });
  const { data: receipts = [] } = useQuery({ queryKey: ['receipts'], queryFn: async () => { const { data } = await api.get('/api/receipts?sort=-date&limit=500'); return data; } });
  const { data: paypalConfig = {} } = useQuery({ queryKey: ['paypal-config'], queryFn: async () => { const { data } = await api.get('/api/payments/config'); return data; } });
  const { data: currencyRates = {} } = useQuery({ queryKey: ['currency-rates'], queryFn: async () => { const { data } = await api.get('/api/currency/rates'); return data; } });
  const { data: accountantShare = {} } = useQuery({ queryKey: ['accountant-share'], queryFn: async () => { const { data } = await api.get('/api/accountant/share'); return data; } });
  const { data: trialStatus = {} } = useQuery({ queryKey: ['trial-status'], queryFn: async () => { const { data } = await api.get('/api/trial/status'); return data; } });
  const { data: billingInvoices = [] } = useQuery({ queryKey: ['billing-invoices'], queryFn: async () => { const { data } = await api.get('/api/billing/invoices'); return data; } });

  const [form, setForm] = useState({});
  const [formInitialized, setFormInitialized] = useState(false);
  const [coachInsights, setCoachInsights] = useState(null);
  const [loadingCoach, setLoadingCoach] = useState(false);
  const [checkoutData, setCheckoutData] = useState(null);
  const [sharingLoading, setSharingLoading] = useState(false);
  const [trialLoading, setTrialLoading] = useState(false);

  // Handle PayPal return
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const paymentId = searchParams.get('paymentId');
    const payerID = searchParams.get('PayerID');

    if (paymentStatus === 'success' && paymentId && payerID) {
      // Execute the payment with retry
      const executePayment = async (attempt = 1) => {
        try {
          await api.post('/api/payments/execute', { paymentId, PayerID: payerID });
          toast.success('Payment completed! Your plan has been upgraded.');
          checkAuth();
          queryClient.invalidateQueries({ queryKey: ['trial-status'] });
          queryClient.invalidateQueries({ queryKey: ['billing-invoices'] });
        } catch (err) {
          if (attempt < 3) {
            // Retry after a short delay
            await new Promise(r => setTimeout(r, 2000));
            return executePayment(attempt + 1);
          }
          toast.error('Payment verification failed. Please contact support with your PayPal transaction ID.');
          console.error('Payment execute failed after retries:', err);
        } finally {
          setSearchParams({});
        }
      };
      executePayment();
    } else if (paymentStatus === 'success') {
      toast.success('Payment completed! Your plan has been upgraded.');
      checkAuth();
      setSearchParams({});
    } else if (paymentStatus === 'cancelled') {
      toast.info('Payment was cancelled.');
      setSearchParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (settings && Object.keys(settings).length > 0 && !formInitialized) {
      setForm(settings);
      setFormInitialized(true);
    }
  }, [settings, formInitialized]);

  const updateMutation = useMutation({
    mutationFn: (d) => api.put('/api/settings', d),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['settings'] }); toast.success('Settings saved!'); }
  });

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const handleSave = () => updateMutation.mutate(form);

  const handleUpgrade = (plan, extraUsers, totalPrice) => {
    setCheckoutData({ plan, extraUsers, totalPrice });
  };

  const startTrial = async (plan) => {
    setTrialLoading(true);
    try {
      await api.post('/api/trial/start', { plan });
      queryClient.invalidateQueries({ queryKey: ['trial-status'] });
      toast.success(`7-day free trial started for ${plan.charAt(0).toUpperCase() + plan.slice(1)} plan!`);
      window.location.reload();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to start trial';
      toast.error(msg);
    }
    setTrialLoading(false);
  };

  const handleShareWithAccountant = async () => {
    const email = form.accountant_email || '';
    if (!email) { toast.error('Please enter an accountant email first'); return; }
    setSharingLoading(true);
    try {
      const { data } = await api.post('/api/accountant/share', { email });
      queryClient.invalidateQueries({ queryKey: ['accountant-share'] });
      toast.success('Accountant portal link created!');
      navigator.clipboard.writeText(data.share_url);
      toast.info('Link copied to clipboard');
    } catch { toast.error('Failed to create share link'); }
    setSharingLoading(false);
  };

  const handleRevokeShare = async () => {
    try {
      await api.delete('/api/accountant/share');
      queryClient.invalidateQueries({ queryKey: ['accountant-share'] });
      toast.success('Access revoked');
    } catch { toast.error('Failed to revoke'); }
  };

  const copyShareLink = () => {
    if (accountantShare?.share_url) {
      navigator.clipboard.writeText(accountantShare.share_url);
      toast.success('Link copied to clipboard');
    }
  };

  const getCoachInsights = async () => {
    setLoadingCoach(true);
    try {
      const total = receipts.reduce((s, r) => s + (r.amount || 0), 0);
      const cats = receipts.reduce((acc, r) => { acc[r.category] = (acc[r.category] || 0) + (r.amount || 0); return acc; }, {});
      const { data } = await api.post('/api/ai/coach', {
        financial_data: {
          totalReceipts: receipts.length,
          totalSpent: total,
          avgPerReceipt: receipts.length > 0 ? total / receipts.length : 0,
          topCategories: Object.entries(cats).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([k, v]) => ({ category: k, total: v })),
          taxDeductible: receipts.filter(r => r.tax_deductible).reduce((s, r) => s + r.amount * (r.tax_split_percentage || 100) / 100, 0),
          gstCredits: receipts.filter(r => r.gst_type === 'gst_10').reduce((s, r) => s + (r.gst_amount || r.amount / 11), 0),
        }
      });
      setCoachInsights(data);
    } catch (e) { console.error('Coach insights error:', e); }
    setLoadingCoach(false);
  };

  const typeIcons = { save: '🎯', warning: '⚠️', tip: '💡', opportunity: '🚀' };

  const nextBilling = new Date();
  nextBilling.setMonth(nextBilling.getMonth() + 1);
  const nextBillingStr = nextBilling.toISOString().split('T')[0];

  const plans = [
    {
      name: 'Individual', subtitle: 'Free personal expense tracking', price: 'Free', priceNum: 0, popular: false, maxExtraUsers: 0, extraUserRate: 0,
      features: ['25 Smart Scans per month', 'Smart auto-categorisation', 'Basic document vault', 'Secure cloud backup', 'Spending overview dashboard']
    },
    {
      name: 'Basic', subtitle: 'For individuals & small teams', price: '$4.95', priceNum: 4.95, popular: true, maxExtraUsers: 5, extraUserRate: 4.95,
      features: ['Unlimited Smart Scans', 'AI Spending Insights', 'Tax Mode with ATO categories', 'Distance & mileage tracking', 'Up to 2 users included', 'Budget tracking & alerts', 'Bill & subscription management', 'Document vault (10GB storage)']
    },
    {
      name: 'Standard', subtitle: 'Everything in Basic, built for teams', price: '$14.95', priceNum: 14.95, popular: false, maxExtraUsers: 5, extraUserRate: 9.95,
      features: ['Everything in Basic', 'Up to 10 users included', 'Employee expense submissions & approvals', 'Client & project tagging', 'Exportable expense reports', 'Accountant collaboration tools', 'BAS-ready GST summaries', 'Invoice management', 'Priority support']
    },
  ];

  const currentPlan = user?.plan || 'individual';
  const inputClass = "w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 placeholder:text-slate-400";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="settings-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1">Settings</h1>
        <p className="text-slate-500 mb-8">Manage your account and preferences</p>

        <Tabs defaultValue={searchParams.get('payment') ? 'subscription' : 'profile'}>
          <TabsList className="bg-white border border-slate-200 mb-6 w-full h-11">
            <TabsTrigger value="profile" className="text-slate-500 data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900" data-testid="settings-tab-profile">Profile</TabsTrigger>
            <TabsTrigger value="preferences" className="text-slate-500 data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900" data-testid="settings-tab-preferences">Preferences</TabsTrigger>
            <TabsTrigger value="notifications" className="text-slate-500 data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900" data-testid="settings-tab-notifications">Notifications</TabsTrigger>
            <TabsTrigger value="security" className="text-slate-500 data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900" data-testid="settings-tab-security">Security</TabsTrigger>
            <TabsTrigger value="subscription" className="text-slate-500 data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900" data-testid="settings-tab-subscription">Subscription</TabsTrigger>
            <TabsTrigger value="coach" className="text-slate-500 data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900" data-testid="settings-tab-coach">AI Coach</TabsTrigger>
          </TabsList>

          {/* ── Profile Tab ── */}
          <TabsContent value="profile" data-testid="settings-profile-tab">
            <div className="bg-white rounded-2xl border border-slate-100 p-6 lg:p-8">
              <h3 className="text-lg font-bold text-slate-900 mb-6">Profile Information</h3>
              <div className="flex items-center gap-4 mb-8">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white font-bold text-2xl flex-shrink-0">
                  {user?.name?.[0]?.toLowerCase() || 'u'}
                </div>
                <div>
                  <p className="font-semibold text-slate-900">{user?.name || 'User'}</p>
                  <p className="text-sm text-slate-500">{user?.email}</p>
                </div>
              </div>
              <div className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-1.5">Full Name</label>
                    <input value={form.full_name || user?.name || ''} onChange={e => handleChange('full_name', e.target.value)} className={inputClass} data-testid="settings-full-name" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-1.5">Email</label>
                    <input value={user?.email || ''} disabled className={`${inputClass} bg-slate-50 text-slate-500`} />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-1.5">Company Name <span className="text-slate-400 font-normal text-xs">(Optional)</span></label>
                    <input value={form.company_name || ''} onChange={e => handleChange('company_name', e.target.value)} className={inputClass} placeholder="Your company name" data-testid="settings-company-name" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-1.5">Phone Number <span className="text-slate-400 font-normal text-xs">(Optional)</span></label>
                    <input value={form.company_phone || ''} onChange={e => handleChange('company_phone', e.target.value)} className={inputClass} placeholder="0433556677" data-testid="settings-phone" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-1.5">ABN Number <span className="text-slate-400 font-normal text-xs">(Optional)</span></label>
                    <input value={form.company_abn || ''} onChange={e => handleChange('company_abn', e.target.value)} className={inputClass} placeholder="XX XXX XXX XXX" data-testid="settings-abn" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-1.5">Accountant Email <span className="text-slate-400 font-normal text-xs">(Optional)</span></label>
                    <input type="email" value={form.accountant_email || ''} onChange={e => handleChange('accountant_email', e.target.value)} className={inputClass} placeholder="accountant@example.com" data-testid="settings-accountant-email" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-1.5">Address <span className="text-slate-400 font-normal text-xs">(Optional)</span></label>
                  <input value={form.company_address || ''} onChange={e => handleChange('company_address', e.target.value)} className={inputClass} placeholder="123 Main St, Sydney NSW 2000" data-testid="settings-address" />
                </div>
              </div>
              <button onClick={handleSave} className="flex items-center gap-2 mt-8 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-semibold transition-colors" data-testid="save-profile-button">
                <Save className="w-4 h-4" />Save Profile
              </button>
            </div>

            {/* Accountant Portal Sharing */}
            <div className="bg-white rounded-2xl border border-slate-100 p-6 lg:p-8 mt-6" data-testid="accountant-sharing-section">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center"><Share2 className="w-5 h-5 text-violet-600" /></div>
                <div><h3 className="text-lg font-bold text-slate-900">Accountant Portal</h3><p className="text-sm text-slate-500">Share read-only access to your financial data</p></div>
              </div>

              {accountantShare?.active ? (
                <div className="space-y-3">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                    <p className="text-sm font-medium text-emerald-800 mb-1">Active share link</p>
                    <div className="flex items-center gap-2">
                      <code className="text-xs bg-white border border-emerald-200 rounded-lg px-3 py-2 flex-1 text-slate-600 truncate" data-testid="accountant-share-url">{accountantShare.share_url}</code>
                      <button onClick={copyShareLink} className="p-2 bg-white border border-emerald-200 rounded-lg hover:bg-emerald-50 transition-colors" title="Copy link"><Copy className="w-4 h-4 text-emerald-600" /></button>
                      <a href={accountantShare.share_url} target="_blank" rel="noreferrer" className="p-2 bg-white border border-emerald-200 rounded-lg hover:bg-emerald-50 transition-colors" title="Open portal"><ExternalLink className="w-4 h-4 text-emerald-600" /></a>
                    </div>
                    <p className="text-xs text-emerald-600 mt-2">Shared with: {accountantShare.accountant_email} — Expires: {accountantShare.expires_at?.split('T')[0]}</p>
                  </div>
                  <button onClick={handleRevokeShare} className="flex items-center gap-2 px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl text-sm font-medium transition-colors" data-testid="revoke-accountant-share">
                    <Trash2 className="w-4 h-4" /> Revoke Access
                  </button>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-slate-500 mb-3">Enter your accountant's email above, then generate a secure read-only link. They'll be able to view receipts, invoices, tax summaries, and documents without logging in.</p>
                  <button onClick={handleShareWithAccountant} disabled={sharingLoading} className="flex items-center gap-2 px-5 py-2.5 bg-violet-500 hover:bg-violet-600 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50" data-testid="share-with-accountant-button">
                    {sharingLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                    Generate Share Link
                  </button>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ── Preferences Tab ── */}
          <TabsContent value="preferences" data-testid="settings-preferences-tab">
            <div className="bg-white rounded-2xl border border-slate-100 p-6 lg:p-8">
              <h3 className="text-lg font-bold text-slate-900 mb-6">App Preferences</h3>
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center"><Sun className="w-5 h-5 text-slate-600" /></div>
                    <div><p className="font-semibold text-slate-900">Theme</p><p className="text-sm text-slate-500">Choose your preferred appearance</p></div>
                  </div>
                  <div className="relative">
                    <select value={form.theme || 'light'} onChange={e => { handleChange('theme', e.target.value); updateMutation.mutate({ ...form, theme: e.target.value }); }} className="appearance-none bg-white border border-slate-200 rounded-xl px-4 py-2.5 pr-9 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 cursor-pointer" data-testid="settings-theme-select">
                      <option value="light">Light</option><option value="dark">Dark</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                </div>
                <hr className="border-slate-100" />
                <div className="flex items-center justify-between">
                  <div><p className="font-semibold text-slate-900">Auto-categorise Receipts</p><p className="text-sm text-slate-500">Use AI to automatically categorise scanned receipts</p></div>
                  <Switch checked={form.auto_categorise !== false} onCheckedChange={v => { handleChange('auto_categorise', v); updateMutation.mutate({ ...form, auto_categorise: v }); }} data-testid="settings-auto-categorise" />
                </div>
                <hr className="border-slate-100" />
                <div>
                  <p className="font-semibold text-slate-900 mb-2">Default Tax Split Percentage</p>
                  <div className="relative">
                    <select value={form.default_tax_split || '100'} onChange={e => { handleChange('default_tax_split', e.target.value); updateMutation.mutate({ ...form, default_tax_split: e.target.value }); }} className="w-full appearance-none bg-white border border-slate-200 rounded-xl px-4 py-3 pr-9 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 cursor-pointer" data-testid="settings-tax-split">
                      <option value="100">100% (Fully deductible)</option><option value="75">75%</option><option value="50">50%</option><option value="25">25%</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                </div>

                <hr className="border-slate-100" />

                {/* Currency */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center"><Globe className="w-5 h-5 text-emerald-600" /></div>
                    <div><p className="font-semibold text-slate-900">Display Currency</p><p className="text-sm text-slate-500">Currency used across the app</p></div>
                  </div>
                  <div className="relative">
                    <select value={form.currency || 'AUD'} onChange={e => { handleChange('currency', e.target.value); updateMutation.mutate({ ...form, currency: e.target.value }); }} className="appearance-none bg-white border border-slate-200 rounded-xl px-4 py-2.5 pr-9 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 cursor-pointer" data-testid="settings-currency-select">
                      {currencyRates.rates && Object.keys(currencyRates.rates).map(c => (
                        <option key={c} value={c}>{c}{c !== 'AUD' ? ` (1 AUD = ${currencyRates.rates[c]})` : ' (Default)'}</option>
                      ))}
                      {!currencyRates.rates && <option value="AUD">AUD (Default)</option>}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ── Notifications Tab ── */}
          <TabsContent value="notifications" data-testid="settings-notifications-tab">
            <div className="bg-white rounded-2xl border border-slate-100 p-6 lg:p-8">
              <h3 className="text-lg font-bold text-slate-900 mb-6">Notification Preferences</h3>
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center"><Bell className="w-5 h-5 text-amber-500" /></div>
                    <div><p className="font-semibold text-slate-900">Budget Alerts</p><p className="text-sm text-slate-500">Get notified when nearing budget limits</p></div>
                  </div>
                  <Switch checked={form.budget_alerts !== false} onCheckedChange={v => { handleChange('budget_alerts', v); updateMutation.mutate({ ...form, budget_alerts: v }); }} data-testid="settings-budget-alerts" />
                </div>
                <hr className="border-slate-100" />
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center"><CheckSquare className="w-5 h-5 text-blue-500" /></div>
                    <div><p className="font-semibold text-slate-900">Weekly Summary</p><p className="text-sm text-slate-500">Receive a weekly spending summary email</p></div>
                  </div>
                  <Switch checked={form.weekly_summary === true} onCheckedChange={v => { handleChange('weekly_summary', v); updateMutation.mutate({ ...form, weekly_summary: v }); }} data-testid="settings-weekly-summary" />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ── Security Tab ── */}
          <TabsContent value="security" data-testid="settings-security-tab">
            <SecurityTab />
          </TabsContent>

          {/* ── Subscription Tab ── */}
          <TabsContent value="subscription" data-testid="settings-subscription-tab">
            {/* Current Plan Banner */}
            <div className={`rounded-2xl p-6 mb-6 text-white ${trialStatus.on_trial ? 'bg-gradient-to-r from-amber-500 to-orange-500' : 'bg-gradient-to-r from-emerald-500 to-teal-500'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${trialStatus.on_trial ? 'text-amber-100' : 'text-emerald-100'}`}>Current Plan</p>
                  <p className="text-2xl font-bold capitalize">{currentPlan}</p>
                  {trialStatus.on_trial ? (
                    <div className="flex items-center gap-2 mt-2 text-amber-100 text-sm">
                      <Clock className="w-4 h-4" />
                      {trialStatus.days_remaining} day{trialStatus.days_remaining !== 1 ? 's' : ''} remaining in trial — subscribe to keep access
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 mt-2 text-emerald-100 text-sm"><CreditCard className="w-4 h-4" />
                      {currentPlan === 'individual' ? 'Free forever' : `Next billing: ${nextBillingStr}`}
                    </div>
                  )}
                </div>
                <span className={`backdrop-blur-sm text-white px-4 py-1.5 rounded-full text-sm font-medium ${trialStatus.on_trial ? 'bg-white/20' : 'bg-white/20'}`}>
                  {trialStatus.on_trial ? `Trial — ${trialStatus.days_remaining}d left` : trialStatus.plan_status === 'paid' ? 'Paid' : 'Active'}
                </span>
              </div>
            </div>

            {/* Cancel / Downgrade Options for paid users */}
            {currentPlan !== 'individual' && (
              <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-slate-900">Manage Subscription</p>
                    <p className="text-sm text-slate-500 mt-0.5">
                      {currentPlan === 'standard' ? 'Downgrade to Basic or cancel to Individual (Free)' : 'Cancel to switch back to Individual (Free)'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {currentPlan === 'standard' && (
                      <button
                        onClick={async () => {
                          if (!window.confirm('Downgrade to Basic? You will lose access to Standard features like Invoices and Employee management.')) return;
                          try {
                            await api.post('/api/subscription/downgrade', { plan: 'basic' });
                            toast.success('Downgraded to Basic plan');
                            checkAuth();
                            window.location.reload();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Downgrade failed');
                          }
                        }}
                        className="px-4 py-2 bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 rounded-xl text-sm font-medium transition-colors"
                        data-testid="downgrade-to-basic-button"
                      >
                        Downgrade to Basic
                      </button>
                    )}
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Cancel your ${currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)} subscription? You will be moved to the Individual (Free) plan and lose access to premium features.`)) return;
                        try {
                          await api.post('/api/subscription/cancel');
                          toast.success('Subscription cancelled. You are now on the Individual (Free) plan.');
                          checkAuth();
                          window.location.reload();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Cancellation failed');
                        }
                      }}
                      className="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-xl text-sm font-medium transition-colors"
                      data-testid="cancel-subscription-button"
                    >
                      Cancel Subscription
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Trial info banner for Individual users who haven't tried yet */}
            {currentPlan === 'individual' && !trialStatus.trial_used && (
              <div className="bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-2xl p-5 mb-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-6 h-6 text-violet-600" />
                </div>
                <div>
                  <p className="font-semibold text-violet-900">Try Basic or Standard free for 7 days</p>
                  <p className="text-sm text-violet-600">Unlock all features with no commitment. No credit card required. Automatically reverts to Individual after trial ends.</p>
                </div>
              </div>
            )}

            <h3 className="text-lg font-bold text-slate-900 mb-4">Available Plans</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {plans.map(plan => (
                <PlanCard key={plan.name} plan={plan} currentPlan={currentPlan} onUpgrade={handleUpgrade} onStartTrial={startTrial} trialUsed={trialStatus.trial_used} trialLoading={trialLoading} planStatus={trialStatus.plan_status} />
              ))}
            </div>

            {/* ── Billing History ── */}
            {billingInvoices.length > 0 && (
              <div className="mt-8" data-testid="billing-history-section">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Billing History</h3>
                <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-xs text-slate-500 uppercase tracking-wider bg-slate-50 border-b border-slate-200">
                        <th className="px-5 py-3">Invoice</th>
                        <th className="px-5 py-3">Date</th>
                        <th className="px-5 py-3">Plan</th>
                        <th className="px-5 py-3">Period</th>
                        <th className="px-5 py-3">Amount</th>
                        <th className="px-5 py-3">Status</th>
                        <th className="px-5 py-3 text-right">PDF</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {billingInvoices.map((inv) => (
                        <tr key={inv.id} className="hover:bg-slate-50 transition-colors" data-testid={`billing-invoice-${inv.invoice_number}`}>
                          <td className="px-5 py-3.5 text-sm font-medium text-slate-900">{inv.invoice_number}</td>
                          <td className="px-5 py-3.5 text-sm text-slate-600">{inv.issued_date?.split('T')[0]}</td>
                          <td className="px-5 py-3.5">
                            <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 font-medium capitalize">{inv.plan}</span>
                            {inv.extra_users > 0 && <span className="text-xs text-slate-400 ml-1.5">+{inv.extra_users} user{inv.extra_users > 1 ? 's' : ''}</span>}
                          </td>
                          <td className="px-5 py-3.5 text-sm text-slate-500">{inv.billing_period_start?.split('T')[0]} — {inv.billing_period_end?.split('T')[0]}</td>
                          <td className="px-5 py-3.5 text-sm font-semibold text-slate-900">{fmt(inv.total)}</td>
                          <td className="px-5 py-3.5">
                            <span className="text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-600 font-medium">Paid</span>
                          </td>
                          <td className="px-5 py-3.5 text-right">
                            <button
                              onClick={async () => {
                                try {
                                  const response = await api.get(`/api/billing/invoices/${inv.id}/pdf`, { responseType: 'blob' });
                                  const url = window.URL.createObjectURL(new Blob([response.data]));
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.download = `ExpensIQ-Invoice-${inv.invoice_number}.pdf`;
                                  link.click();
                                  window.URL.revokeObjectURL(url);
                                } catch { toast.error('Failed to download invoice'); }
                              }}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-medium transition-colors"
                              data-testid={`download-invoice-${inv.invoice_number}`}
                            >
                              <Download className="w-3.5 h-3.5" /> PDF
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-slate-400 mt-3">All amounts are in AUD and inclusive of 10% GST. ExpensIQ Pty Ltd, Sydney, Australia.</p>
              </div>
            )}
          </TabsContent>

          {/* ── AI Coach Tab ── */}
          <TabsContent value="coach" data-testid="settings-coach-tab">
            <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl border border-violet-200 p-6">
              <div className="flex items-center gap-3 mb-4"><Sparkles className="w-5 h-5 text-violet-600" /><h3 className="font-semibold text-violet-900">AI Financial Coach</h3></div>
              <p className="text-sm text-violet-700 mb-4">Get personalised financial insights powered by AI analysis of your spending patterns.</p>
              <button onClick={getCoachInsights} disabled={loadingCoach || receipts.length === 0} className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50" data-testid="get-coach-insights-button">
                {loadingCoach ? <><Loader2 className="w-4 h-4 animate-spin" />Analyzing...</> : <><Sparkles className="w-4 h-4" />Get Coaching</>}
              </button>
              {receipts.length === 0 && <p className="text-sm text-violet-500 mt-2">Add some receipts first to get insights.</p>}
            </div>
            {coachInsights?.insights && (
              <div className="space-y-3 mt-6">
                {coachInsights.insights.map((insight, i) => (
                  <div key={i} className="bg-white rounded-xl border border-slate-100 p-5">
                    <div className="flex items-start gap-3">
                      <span className="text-xl">{typeIcons[insight.type] || '💡'}</span>
                      <div>
                        <h4 className="font-semibold text-slate-900">{insight.title}</h4>
                        <p className="text-sm text-slate-600 mt-1">{insight.body}</p>
                        {insight.saving && <p className="text-sm text-emerald-600 font-medium mt-2">Potential saving: {insight.saving}</p>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* PayPal Checkout Modal */}
      <AnimatePresence>
        {checkoutData && (
          <PayPalCheckoutModal
            plan={checkoutData.plan}
            extraUsers={checkoutData.extraUsers}
            totalPrice={checkoutData.totalPrice}
            onClose={() => setCheckoutData(null)}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['settings'] });
              window.location.reload();
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
